import chainsData from "../chains.json";

export interface ManualToken {
  address: `0x${string}`;
  symbol: string;
  decimals: number;
  name: string;
  logoUrl?: string;
}

/**
 * Known integration tags that can appear in `ChainDefinition.integrations`.
 * Adding a new integration → extend this union. Consumers calling
 * `chainsByIntegration("foo")` get autocomplete + typo safety.
 */
export type KnownIntegration =
  | "aerodrome"
  | "anyspend"
  | "cow-swap"
  | "evm-log"
  | "morpho"
  | "railgun"
  | "x402"
  | "zerodev";

export interface ChainDefinition {
  chainId: number;
  key: string;
  name: string;
  network: string;
  type: "evm" | "solana";
  isTestnet: boolean;
  integrations: KnownIntegration[];
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  explorer: string;
  rpcEnvKey: string | null;
  fallbackRpc: string | null;
  quicknodeSlug: string | null;
  quicknodeRpcPath?: string;
  coingecko: {
    platform: string;
    nativeCoinId: string;
  } | null;
  /**
   * GeckoTerminal network slug (differs from `coingecko.platform`: GT uses
   * "eth", CoinGecko uses "ethereum"). Used for on-chain pool lookups and
   * OHLCV charts. Null when GeckoTerminal does not index this chain.
   */
  geckoTerminalNetwork?: string | null;
  trustwalletSlug: string | null;
  logoUrl: string | null;
  tags: string[];
  manualTokens?: ManualToken[];
  /**
   * Wrapped-native ERC20 address (e.g. WETH on Ethereum, WPLUME on Plume).
   * Used for GeckoTerminal pool lookups on the native coin and for DEX
   * routing that needs an ERC20 form of the gas token.
   */
  wrappedNative?: `0x${string}`;
}

export const chains: ChainDefinition[] = chainsData as ChainDefinition[];

export const evmChains = chains.filter(c => c.type === "evm");

export const chainById = new Map(chains.map(c => [c.chainId, c]));

export const CHAIN_IDS: Record<string, number> = Object.fromEntries(chains.map(c => [c.key, c.chainId]));

export const CHAIN_NAMES: Record<number, string> = Object.fromEntries(chains.map(c => [c.chainId, c.name]));

export const CHAIN_ID_TO_NETWORK: Record<number, string> = Object.fromEntries(chains.map(c => [c.chainId, c.network]));

export const FALLBACK_RPC_URLS: Record<number, string> = Object.fromEntries(
  chains.filter(c => c.fallbackRpc).map(c => [c.chainId, c.fallbackRpc!]),
);

export const BLOCK_EXPLORERS: Record<number, string> = Object.fromEntries(chains.map(c => [c.chainId, c.explorer]));

export const COINGECKO_MAPPINGS: Record<number, { platform: string; nativeCoinId: string }> = Object.fromEntries(
  chains.filter(c => c.coingecko).map(c => [c.chainId, c.coingecko!]),
);

export const TRUSTWALLET_SLUGS: Record<number, string> = Object.fromEntries(
  chains.filter(c => c.trustwalletSlug).map(c => [c.chainId, c.trustwalletSlug!]),
);

export const CHAIN_TAG_MAP: Record<string, number> = Object.fromEntries(
  chains.flatMap(c => c.tags.map(tag => [tag, c.chainId])),
);

export const LOGO_URLS: Record<number, string> = Object.fromEntries(
  chains.filter(c => c.logoUrl).map(c => [c.chainId, c.logoUrl!]),
);

export function buildQuicknodeUrl(subdomain: string, key: string, chain: ChainDefinition): string | null {
  if (chain.quicknodeSlug === null) return null;
  const network = chain.quicknodeSlug === "" ? "" : `.${chain.quicknodeSlug}`;
  const path = chain.quicknodeRpcPath ?? "";
  return `https://${subdomain}${network}.quiknode.pro/${key}${path}`;
}

export function buildQuicknodeRpcMap(subdomain: string, key: string): Record<number, string> {
  return Object.fromEntries(
    chains.filter(c => c.quicknodeSlug !== null).map(c => [c.chainId, buildQuicknodeUrl(subdomain, key, c)!]),
  );
}

export function chainsByIntegration(integration: KnownIntegration): ChainDefinition[] {
  return chains.filter(c => c.integrations.includes(integration));
}

export function chainIdsByIntegration(integration: KnownIntegration): number[] {
  return chainsByIntegration(integration).map(c => c.chainId);
}

export function chainNamesByIntegration(integration: KnownIntegration): Record<number, string> {
  return Object.fromEntries(chainsByIntegration(integration).map(c => [c.chainId, c.name]));
}

export const ZERODEV_SUPPORTED_CHAINS: ChainDefinition[] = chainsByIntegration("zerodev");

/**
 * Chains tagged with the "anyspend" integration AND of EVM type — the set
 * consumed by SDK `supportedChains` / `supportedChainsTW` for AnySpend's
 * wallet-switch gate (`useUnifiedChainSwitchAndExecute`). Solana (also tagged
 * anyspend) is excluded here because it's handled by Phantom-based flows that
 * key off `RELAY_SOLANA_MAINNET_CHAIN_ID`, not viem.
 */
export const ANYSPEND_EVM_CHAINS: ChainDefinition[] = chainsByIntegration("anyspend").filter(c => c.type === "evm");

// Validate at module init: every anyspend EVM chain must have a fallbackRpc.
// The SDK transformers (toViemChain / toThirdwebChain) assume this invariant
// holds — catching a missing RPC here is far better than throwing on first
// pay-button click in production.
{
  const missingRpc = ANYSPEND_EVM_CHAINS.filter(c => !c.fallbackRpc);
  if (missingRpc.length > 0) {
    throw new Error(
      `chain-registry: anyspend EVM chains missing fallbackRpc: ${missingRpc.map(c => `${c.chainId} (${c.key})`).join(", ")}`,
    );
  }
}

/**
 * Chain IDs that the default indexer (Sim Dune) does not support.
 * These chains require RPC + CoinGecko fallback for balance/metadata.
 */
export const MANUAL_CHAIN_IDS: ReadonlySet<number> = new Set(
  chains.filter(c => c.manualTokens && c.manualTokens.length > 0).map(c => c.chainId),
);

export function hasManualTokens(chainId: number): boolean {
  return MANUAL_CHAIN_IDS.has(chainId);
}

export function getManualTokens(chainId: number): ManualToken[] {
  return chainById.get(chainId)?.manualTokens ?? [];
}

export function findManualToken(chainId: number, address: string): ManualToken | null {
  const lower = address.toLowerCase();
  const tokens = getManualTokens(chainId);
  return tokens.find(t => t.address.toLowerCase() === lower) ?? null;
}
